import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRecovery, setIsRecovery] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (!data.session) setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      if (event === 'PASSWORD_RECOVERY') setIsRecovery(true);
      if (!newSession) {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session?.user) return;
    setLoading(true);
    supabase
      .from('profiles')
      .select('id, email, name, role, is_active')
      .eq('id', session.user.id)
      .single()
      .then(({ data, error }) => {
        if (error) { console.error('Error loading profile:', error); setProfile(null); }
        else setProfile(data);
        setLoading(false);
      });
  }, [session?.user?.id]);

  const signIn = async (email, password, remember = true) => {
    // Tell the custom storage adapter whether to use localStorage or sessionStorage
    // for the session token that Supabase is about to write.
    localStorage.setItem('luye_remember', remember ? 'true' : 'false');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error };
    return { data };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const sendPasswordReset = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    return { error };
  };

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error };
  };

  const clearRecovery = () => setIsRecovery(false);

  return (
    <AuthContext.Provider value={{ session, profile, loading, isRecovery, signIn, signOut, sendPasswordReset, updatePassword, clearRecovery }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
